import { Fragment, UIEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { TableCell, TableRow } from '@mui/material'
import { Row, Table as ReactTableType } from '@tanstack/react-table'
import { defaultRangeExtractor, Range, useVirtualizer } from '@tanstack/react-virtual'
import { debounce } from 'lodash-es'
import { useShallow } from 'zustand/react/shallow'

import {
  BodyRow,
  fetchMoreOnBottomReached,
  getColumnWidth,
  HeadRow,
  InfinityProps,
  InstanceTransferProps,
  LoadingBodyRow,
  VirtualizedProps,
  ActionMenu as ActionMenuWrapper,
  Wrapper,
  Root,
  Head,
  Body,
  ScrollToTopButton,
} from '@shared/components/Table'
import {
  useScrollRestorationStore,
  selectScrollRestorationSetters,
  useSelectLastKnownScrollPosition,
} from 'stores/scrollRestoration'

type InfiniteVirtualizedInstanceProps<D> = {
  tableInstance: ReactTableType<D>
  enableSelection: boolean
} & InstanceTransferProps<D> &
  InfinityProps['infinityProps'] &
  VirtualizedProps['virtualizedProps']

const InfiniteVirtualizedInstanceRaw = <D,>({
  tableInstance,
  enableSelection,
  enableLinking,
  LinkProps,
  excludeFieldsFromLinking,
  enableClickAction,
  clickAction,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isInitialLoadingGlobal,
  isInitialLoadingByRowByID,
  RowActions,
  ActionMenuProps,
  estimatedSize = 56,
  getVirtualItems,
  renderSubComponent,
  hideHeader,
  onScroll,
  ScrollTopButton,
  rowGrouping,
}: InfiniteVirtualizedInstanceProps<D>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const selectedRow = useRef<Row<D>>(undefined)
  const parentRef = useRef<HTMLDivElement>(null)

  const { setLastKnownScrollPosition } = useScrollRestorationStore(useShallow(selectScrollRestorationSetters))
  const lastKnownScrollPosition = useRef(useSelectLastKnownScrollPosition(window.location.pathname))

  const { rows } = tableInstance.getRowModel()

  // `starts` is keyed by the index of the row that OPENS each run, so the virtualizer can charge that
  // one row for the header above it and leave every other row at the plain estimate. `startOfRow`
  // points every grouped row back at that opening row.
  const grouping = useMemo(() => {
    if (!rowGrouping) {
      return undefined
    }

    const starts = new Map<number, { groupKey: string; rows: Row<D>[] }>()
    const startOfRow: (number | undefined)[] = []
    let open: { groupKey: string; rows: Row<D>[]; start: number } | undefined

    rows.forEach((row, index) => {
      const groupKey = rowGrouping.getGroupKey(row)

      if (groupKey === undefined) {
        open = undefined
        return
      }

      if (!open || open.groupKey !== groupKey) {
        open = { groupKey, rows: [], start: index }
        starts.set(index, open)
      }

      open.rows.push(row)
      startOfRow[index] = open.start
    })

    return { starts, startOfRow }
  }, [rowGrouping, rows])

  const groupStarts = grouping?.starts

  const groupHeaderSize = rowGrouping?.headerSize ?? 0

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => parentRef.current,
    count: rows.length,
    overscan: 10,
    estimateSize: useCallback(
      (index: number) => estimatedSize + (groupStarts?.has(index) ? groupHeaderSize : 0),
      [estimatedSize, groupHeaderSize, groupStarts],
    ),
    // The sticky header lives with the row that opens its run, so that row has to stay mounted for as
    // long as any of its run is on screen, or the header unmounts with it once it drifts out past
    // the overscan.
    rangeExtractor: useCallback(
      (range: Range) => {
        const indexes = defaultRangeExtractor(range)
        const activeStart = grouping?.startOfRow[range.startIndex]

        return activeStart === undefined || indexes.includes(activeStart) ? indexes : [activeStart, ...indexes]
      },
      [grouping],
    ),
  })
  const { getVirtualItems: getVirtualRows, getTotalSize, scrollToOffset, measure } = rowVirtualizer
  const virtualizedRows = getVirtualRows()

  const paddingTop = virtualizedRows.length > 0 ? virtualizedRows?.[0]?.start || 0 : 0
  const paddingBottom =
    virtualizedRows.length > 0 ? getTotalSize() - (virtualizedRows?.[virtualizedRows.length - 1]?.end || 0) : 0

  const fetchMore = useCallback(
    () =>
      fetchMoreOnBottomReached({
        containerRefElement: parentRef.current,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
      }),
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  const debouncedHandleScroll = useMemo(
    () =>
      debounce((value: number) => {
        setLastKnownScrollPosition(window.location.pathname, value)
      }, 500),
    [setLastKnownScrollPosition],
  )

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      fetchMore()
      onScroll?.(parentRef.current?.scrollTop || 0)

      return debouncedHandleScroll(e.currentTarget.scrollTop)
    },
    [debouncedHandleScroll, fetchMore, onScroll],
  )

  const handleActionMenuClose = useCallback(() => {
    setAnchorEl(undefined)

    setTimeout(() => {
      selectedRow.current = undefined
    }, 300)
  }, [])

  const columnWidth = useMemo(
    () => getColumnWidth({ tableInstance, enableSelection, enableActionColumn: !!RowActions }),
    [RowActions, enableSelection, tableInstance],
  )

  useLayoutEffect(() => {
    if (lastKnownScrollPosition.current) {
      scrollToOffset(lastKnownScrollPosition.current)
    }
  }, [scrollToOffset])

  useEffect(() => {
    fetchMore()
  }, [fetchMore])

  useEffect(() => {
    getVirtualItems(virtualizedRows)
  }, [getVirtualItems, virtualizedRows])

  // estimateSize is not part of the virtualizer's measurement memo, so a header appearing or going
  // away (grouping toggled, a new week loaded) only lands once the cache is dropped. Keyed on where
  // the runs open rather than on the Map, so a refetch that moves nothing leaves the cache alone.
  const groupStartsSignature = groupStarts ? [...groupStarts.keys()].join(',') : undefined

  useLayoutEffect(() => {
    measure()
  }, [groupStartsSignature, measure])

  const renderGroupHeader = (index: number) => {
    const group = groupStarts?.get(index)

    return group ? rowGrouping?.renderHeader(group) : null
  }

  return (
    <Wrapper ref={parentRef} onScroll={handleScroll}>
      {parentRef.current?.scrollTop && parentRef.current.scrollTop > 0 ? (
        ScrollTopButton ? (
          ScrollTopButton(parentRef)
        ) : (
          <ScrollToTopButton
            onClick={() => {
              parentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          />
        )
      ) : null}
      <Root>
        {!hideHeader && (
          <Head>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <HeadRow
                key={headerGroup.id}
                tableInstance={tableInstance}
                row={headerGroup}
                columnWidth={columnWidth}
                enableSelection={enableSelection}
                enableActionColumn={!!RowActions}
                isInitialLoading={isInitialLoadingGlobal}
              />
            ))}
          </Head>
        )}
        {isInitialLoadingGlobal ? (
          <Body>
            {[...Array(15)].map((_, idx) => (
              <LoadingBodyRow
                key={idx}
                tableInstance={tableInstance}
                columnWidth={columnWidth}
                enableSelection={enableSelection}
                enableActionColumn={!!RowActions}
              />
            ))}
          </Body>
        ) : (
          <Body height={getTotalSize()}>
            {paddingTop > 0 && (
              <TableRow sx={{ display: 'block' }}>
                <TableCell sx={{ height: `${paddingTop}px` }} />
              </TableRow>
            )}
            {virtualizedRows.map((virtualRow, position) => (
              <Fragment key={virtualRow.key}>
                {/* A row pinned by the range extractor sits far above the rest of the window; this
                    fills the stretch between them so everything below keeps its offset. */}
                {position > 0 && virtualRow.start > (virtualizedRows[position - 1]?.end ?? 0) && (
                  <TableRow sx={{ display: 'block' }}>
                    <TableCell
                      sx={{ height: `${virtualRow.start - (virtualizedRows[position - 1]?.end ?? 0)}px`, p: 0 }}
                    />
                  </TableRow>
                )}
                {renderGroupHeader(virtualRow.index)}
                <BodyRow
                  tableInstance={tableInstance}
                  row={rows[virtualRow.index] as Row<D>}
                  tint={rowGrouping?.getRowTint?.(rows[virtualRow.index] as Row<D>)}
                  columnWidth={columnWidth}
                  isInitialLoadingByRowByID={isInitialLoadingByRowByID}
                  enableSelection={enableSelection}
                  enableLinking={enableLinking}
                  LinkProps={LinkProps}
                  excludeFieldsFromLinking={excludeFieldsFromLinking}
                  enableClickAction={enableClickAction}
                  clickAction={clickAction}
                  AnimatedButtonProps={
                    RowActions
                      ? {
                          open: !!anchorEl && selectedRow.current?.index === virtualRow.index,
                          IconButtonProps: {
                            onClick: (e) => {
                              selectedRow.current = rows[virtualRow.index]
                              const target = e.currentTarget

                              setTimeout(() => {
                                setAnchorEl(target)
                              }, 0)
                            },
                          },
                          IconProps: {
                            fontSize: 'medium',
                          },
                        }
                      : undefined
                  }
                  renderSubComponent={renderSubComponent}
                />
              </Fragment>
            ))}
            {isFetchingNextPage && (
              <BodyRow
                tableInstance={tableInstance}
                row={[...rows].reverse()[0]}
                columnWidth={columnWidth}
                isInitialLoading
              />
            )}
            {paddingBottom > 0 && (
              <TableRow sx={{ display: 'block' }}>
                <TableCell sx={{ height: `${paddingBottom}px` }} />
              </TableRow>
            )}
          </Body>
        )}
      </Root>
      {!!RowActions && (
        <ActionMenuWrapper
          {...(selectedRow.current && ActionMenuProps(selectedRow.current))}
          anchorEl={anchorEl}
          onClose={handleActionMenuClose}
        >
          {!!selectedRow.current && RowActions({ row: selectedRow.current, onClose: handleActionMenuClose })}
        </ActionMenuWrapper>
      )}
    </Wrapper>
  )
}

export const InfiniteVirtualizedInstance = InfiniteVirtualizedInstanceRaw
